import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { PhoneCall, Smartphone, UserRoundPlus, ShieldCheck, Globe2, Wallet, Headphones, Sparkles } from "lucide-react";
import { CountrySelectorBar } from "@/components/connect/CountrySelectorBar";
import { useCountryRates } from "@/hooks/useCountryRates";
import { useCountryRateBreakdown } from "@/hooks/useCountryRateBreakdown";
import { getFlagSvgUrl } from "@/lib/country-flags";
import { useAppApiRate, formatAudRatePerMin } from "@/hooks/useAppApiRate";
import { sampleForCountry } from "@/lib/authoritative-rate-samples";
import { storeAvailabilityLine } from "@/lib/store-platform";

export interface CountryDetailFAQ {
  question: string;
  answer: string;
}

export interface CountryDetailProps {
  /** ISO-2 (e.g. "ET") - optional. If provided, used by CountrySelectorBar via slug. */
  slug?: string;
  countryName: string;
  /** Dial code without leading + (e.g. "211") */
  dialCode: string;
  /** Best/headline rate in AUD cents per minute */
  ratePerMinuteCents: number;
  /** Optional separate mobile rate in AUD cents */
  mobileRateCents?: number;
  flagEmoji: string;
  story?: string;
  faqs?: CountryDetailFAQ[];
  isAuthenticated: boolean;
  /** Corridor pages: H1 naming the origin, the billing line for that market, the currency to price in. */
  headline?: string;
  billingNote?: string;
  currency?: "AUD" | "USD";
}

export function CountryDetailContent({
  slug,
  countryName,
  dialCode,
  ratePerMinuteCents,
  mobileRateCents,
  flagEmoji,
  story,
  faqs,
  isAuthenticated,
  headline,
  billingNote,
  currency,
}: CountryDetailProps) {
  const { t } = useTranslation("common");
  const { formatRate } = useCountryRates(currency ? { currency } : {});
  const { rows: rawBreakdownRows, isLoading: breakdownLoading } = useCountryRateBreakdown(dialCode, currency ?? "AUD");
  // Shared dial codes (NANP +1, +44 crown deps, etc.) return rows where a
  // single destination_name groups multiple countries (e.g. "Canada /
  // Jamaica / Puerto Rico / United States Fixed"). Keep only rows whose
  // grouped name includes this country, and relabel them so the visible
  // row reads as a clean per-country line.
  const breakdownRows = (() => {
    const lc = countryName.toLowerCase();
    const aliases = [lc];
    if (lc === "united states") aliases.push("usa");
    if (lc === "united kingdom") aliases.push(" uk", "britain", "england");
    const matchesCountry = (name: string) => {
      const n = name.toLowerCase();
      return aliases.some((a) => n.includes(a));
    };
    const matched = rawBreakdownRows
      .filter((r) => matchesCountry(r.destinationName || ""))
      .map((r) => {
        const grouped = (r.destinationName || "").includes("/");
        const typeLabel = r.destinationType.includes("mobile") || r.destinationType.includes("cell")
          ? "Mobile"
          : "Landline";
        return grouped
          ? { ...r, destinationName: `${countryName} ${typeLabel}` }
          : r;
      });
    const finalRows = matched.length > 0 ? matched : rawBreakdownRows;
    // NANP (+1) and many shared codes bill mobile at the same rate as
    // landline. If we only have a fixed/landline row, surface a Mobile
    // line at the same price so visitors don't think mobile is missing.
    const hasMobile = finalRows.some((r) =>
      r.destinationType.includes("mobile") || r.destinationType.includes("cell"),
    );
    const fixedRow = finalRows.find((r) =>
      !(r.destinationType.includes("mobile") || r.destinationType.includes("cell")),
    );
    if (!hasMobile && fixedRow && (dialCode === "1" || dialCode === "44")) {
      return [
        fixedRow,
        {
          ...fixedRow,
          destinationName: `${countryName} Mobile`,
          destinationType: "mobile",
        },
      ];
    }
    return finalRows;
  })();
  const flagUrl = getFlagSvgUrl(flagEmoji);

  const dialPlus = `+${dialCode}`;

  const ctaPrimary = isAuthenticated ? (
    <Link to="/connect-portal/topup">
      <Button size="lg" className="rounded-full px-8">
        {t("topUpNow", "Top up now")}
      </Button>
    </Link>
  ) : (
    <Link to="/connect-portal/auth">
      <Button size="lg" className="rounded-full px-8">
        {t("getStarted")}
      </Button>
    </Link>
  );

  const defaultFaqs: CountryDetailFAQ[] = [
    {
      question: t("genericFaq1Q", { country: countryName }),
      answer: t("genericFaq1A", { country: countryName }),
    },
    {
      question: t("genericFaq2Q", { country: countryName }),
      answer: t("genericFaq2A", { code: dialPlus }),
    },
    {
      question: t("genericFaq3Q", { country: countryName }),
      answer: t("genericFaq3A", { country: countryName }),
    },
    {
      question: t("genericFaq4Q"),
      answer: t("genericFaq4A"),
    },
  ];
  const finalFaqs = faqs && faqs.length > 0 ? faqs : defaultFaqs;

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-border/40 bg-gradient-to-b from-primary/5 via-background to-background">
        <div className="container mx-auto px-4 py-14 md:py-20 text-center">
          <img
            src={flagUrl}
            alt={countryName}
            className="w-16 h-12 object-cover rounded mx-auto mb-5 shadow-md"
          />
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground mb-3">{headline ?? (<>
            {t("callCountry", { country: countryName })}
          </>)}</h1>
          <p className="text-base sm:text-lg text-muted-foreground max-w-2xl mx-auto mb-6">
            {story ?? t("genericCountryStory", { country: countryName })}
          </p>

          {/* Rate snapshot - landline + mobile shown clearly.
              Authenticated users see live App API (ASTPP) values so
              display == billing. Public visitors see legacy voice_routes
              cents until they sign in. */}
          <AuthoritativeRateSnapshot
            dialCode={dialCode}
            landlineCents={ratePerMinuteCents}
            mobileCents={mobileRateCents}
            formatRate={formatRate}
            isAuthenticated={isAuthenticated}
            perMinLabel={t("perMin")}
          />


          <div className="flex flex-wrap items-center justify-center gap-3">
            {ctaPrimary}
            <Link to="/connect-portal/rates">
              <Button size="lg" variant="outline" className="rounded-full px-8">
                {t("viewRates")}
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Pay-as-you-go - full per-carrier breakdown from voice_routes */}
      <section className="py-10 md:py-12">
        <div className="container mx-auto px-4 max-w-3xl">
          <div className="text-center mb-5">
            <h2 className="text-xl sm:text-2xl font-bold tracking-tight">Pay as you go</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Per-minute rates by carrier. The cheapest available route is always used.
            </p>
          </div>

          {breakdownLoading ? (
            <p className="text-sm text-muted-foreground text-center py-6">Loading current rates…</p>
          ) : breakdownRows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">
              Rates for {countryName} are being updated. Please check back shortly.
            </p>
          ) : (
            <div className="rounded-2xl border border-border bg-card overflow-hidden">
              <div className="grid grid-cols-12 px-4 py-3 text-[10px] sm:text-xs uppercase tracking-wide text-muted-foreground border-b border-border bg-muted/40">
                <span className="col-span-6">Destination</span>
                <span className="col-span-3 text-right">Price</span>
                <span className="col-span-3 text-right">$10 buys</span>
              </div>
              {breakdownRows.map((r, i) => {
                const minutesPer10 = r.retailRateAudIncGstCents > 0
                  ? Math.floor(1000 / r.retailRateAudIncGstCents)
                  : 0;
                const isMobile = r.destinationType.includes("mobile") || r.destinationType.includes("cell");
                return (
                  <div
                    key={`${r.destinationName}-${r.destinationType}-${i}`}
                    className="grid grid-cols-12 px-4 py-3 items-center border-b border-border last:border-0"
                  >
                    <div className="col-span-6 flex items-center gap-2 min-w-0">
                      <span
                        className={`inline-flex shrink-0 text-[10px] font-semibold uppercase tracking-wide rounded-full px-2 py-0.5 ${
                          isMobile
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {isMobile ? "Mobile" : "Landline"}
                      </span>
                      <span className="text-sm sm:text-base truncate">{r.destinationName}</span>
                    </div>
                    <span className="col-span-3 text-right font-bold text-primary text-sm sm:text-base">
                      {formatRate(r.retailRateAudIncGstCents)}/min
                    </span>
                    <span className="col-span-3 text-right text-xs sm:text-sm text-muted-foreground">
                      {minutesPer10} min
                    </span>
                  </div>
                );
              })}
            </div>
          )}

          <p className="text-xs text-muted-foreground text-center mt-3">
            {billingNote ?? "Rates shown in Australian dollars. No hidden fees. Credit never expires. No subscription required."}
          </p>

        </div>
      </section>

      {/* How to call */}
      <section className="py-12 md:py-16 bg-muted/30 border-y border-border/40">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center mb-8">
            How to call {countryName}
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                From the elefa Connect dialpad
              </p>
              <p className="font-mono text-base sm:text-lg">
                <span className="text-primary font-bold">{dialPlus}</span>{" "}
                <span className="text-muted-foreground">+ city code + local number</span>
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Tap the <span className="font-medium text-foreground">+</span> on the keypad to
                start an international call.
              </p>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                From your contacts
              </p>
              <p className="text-sm text-muted-foreground">
                Save numbers in international format -{" "}
                <span className="font-mono text-foreground">{dialPlus}…</span> - and elefa Connect
                will route the call automatically.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center mb-8">
            Start in three simple steps
          </h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <StepCard
              icon={<Smartphone className="h-8 w-8" strokeWidth={1.75} />}
              step="1"
              title="Download elefa Connect"
              body={storeAvailabilityLine()}
            />
            <StepCard
              icon={<UserRoundPlus className="h-8 w-8" strokeWidth={1.75} />}
              step="2"
              title="Sign up with your number"
              body="Quick phone verification - no card needed to start."
            />
            <StepCard
              icon={<PhoneCall className="h-8 w-8" strokeWidth={1.75} />}
              step="3"
              title={`Call ${countryName}`}
              body="Top up any amount, dial, and connect in HD."
            />
          </div>
        </div>
      </section>

      {/* Why elefa */}
      <section className="py-12 md:py-16 bg-muted/30 border-y border-border/40">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center mb-8">
            Why call with elefa Connect
          </h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <ValuePill icon={<Globe2 className="h-7 w-7" strokeWidth={1.75} />} title="Worldwide coverage" body="HD voice to mobile and landline numbers in every country we support." />
            <ValuePill icon={<Wallet className="h-7 w-7" strokeWidth={1.75} />} title="No subscriptions" body="Pay only for what you call. Credit never expires." />
            <ValuePill icon={<ShieldCheck className="h-7 w-7" strokeWidth={1.75} />} title="Transparent pricing" body="See the per-minute rate before you dial. No bill shock." />
            <ValuePill icon={<Headphones className="h-7 w-7" strokeWidth={1.75} />} title="Real human support" body="Reach our team when you need help - no scripts, no runaround." />
          </div>
        </div>
      </section>

      {/* Nearby destinations (uses configured slug if available) */}
      {slug && <CountrySelectorBar currentCountry={slug} currency={currency} />}

      {/* FAQ */}
      <section className="py-12 md:py-16">
        <div className="container mx-auto px-4 max-w-2xl">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-center mb-6">
            Frequently asked questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {finalFaqs.map((faq, i) => (
              <AccordionItem key={i} value={`faq-${i}`}>
                <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-12 md:py-16 bg-primary/5 border-t border-border/40">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-3">
            {t("startNow")}
          </h2>
          <p className="text-muted-foreground mb-6">
            {t("signUpGetCredit")}
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {ctaPrimary}
          </div>
        </div>
      </section>

      {/* Emergency disclaimer */}
      <div className="container mx-auto px-4 py-6">
        <div className="text-xs text-center text-muted-foreground max-w-lg mx-auto">
          <span className="text-destructive font-semibold">{t("important")}</span>{" "}
          {t("emergencyWarning")}
        </div>
      </div>
    </div>
  );
}

function RateRow({
  label,
  cents,
  formatRate,
}: {
  label: string;
  cents: number;
  formatRate: (c: number) => string;
}) {
  // $10 = 1000 cents AUD; minutes = 1000 / cents
  const minutesPer10 = cents > 0 ? Math.floor(1000 / cents) : 0;
  return (
    <div className="grid grid-cols-3 px-4 py-4 items-center border-b border-border last:border-0">
      <span className="text-sm sm:text-base">{label}</span>
      <span className="text-right font-bold text-primary">{formatRate(cents)}/min</span>
      <span className="text-right text-sm text-muted-foreground">{minutesPer10} min</span>
    </div>
  );
}

function StepCard({
  icon,
  step,
  title,
  body,
}: {
  icon: React.ReactNode;
  step: string;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 text-center">
      <div className="mx-auto mb-4 inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary ring-1 ring-primary/10">
        {icon}
      </div>
      <span className="block text-[11px] font-semibold uppercase tracking-widest text-primary/70 mb-1">
        Step {step}
      </span>
      <h3 className="font-semibold text-base mb-1">{title}</h3>
      <p className="text-sm text-muted-foreground">{body}</p>
    </div>
  );
}

function ValuePill({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5 flex gap-4 items-start">
      <span className="inline-flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 text-primary ring-1 ring-primary/10">
        {icon}
      </span>
      <div>
        <h3 className="font-semibold mb-1">{title}</h3>
        <p className="text-sm text-muted-foreground">{body}</p>
      </div>
    </div>
  );
}

/**
 * Renders the landline + mobile hero rate tiles. For authenticated users
 * the values come from /app-api/rate (ASTPP, AUD-native, display == billing).
 * Public visitors fall back to the legacy voice_routes-derived cents so we
 * still render *something* on marketing SEO pages.
 */
function AuthoritativeRateSnapshot({
  dialCode,
  landlineCents,
  mobileCents,
  formatRate,
  isAuthenticated,
  perMinLabel,
}: {
  dialCode: string;
  landlineCents: number;
  mobileCents?: number;
  formatRate: (audCents: number) => string;
  isAuthenticated: boolean;
  perMinLabel: string;
}) {
  const landlineSample = sampleForCountry(dialCode, "landline");
  const mobileSample = sampleForCountry(dialCode, "mobile");
  const landlineApi = useAppApiRate(isAuthenticated ? landlineSample : null);
  const mobileApi = useAppApiRate(isAuthenticated ? mobileSample : null);

  const landlineText = isAuthenticated
    ? landlineApi.rateAud != null
      ? formatAudRatePerMin(landlineApi.rateAud).replace("/min", "")
      : landlineApi.isLoading
        ? "…"
        : landlineCents > 0
          ? formatRate(landlineCents)
          : null
    : landlineCents > 0
      ? formatRate(landlineCents)
      : null;

  const legacyMobileCents =
    mobileCents != null && mobileCents > 0
      ? mobileCents
      : (dialCode === "1" || dialCode === "44") && landlineCents > 0
        ? landlineCents
        : 0;

  const mobileText = isAuthenticated
    ? mobileApi.rateAud != null
      ? formatAudRatePerMin(mobileApi.rateAud).replace("/min", "")
      : mobileApi.isLoading
        ? "…"
        : legacyMobileCents > 0
          ? formatRate(legacyMobileCents)
          : null
    : legacyMobileCents > 0
      ? formatRate(legacyMobileCents)
      : null;

  if (!landlineText && !mobileText) return null;

  return (
    <div className="flex flex-wrap items-stretch justify-center gap-3 mb-6">
      {landlineText && (
        <div className="rounded-2xl border border-border bg-card px-5 py-3 text-left min-w-[140px]">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Landline</p>
          <p className="text-2xl sm:text-3xl font-bold text-foreground leading-tight">
            {landlineText}
            <span className="text-xs text-muted-foreground font-normal ml-1">{perMinLabel}</span>
          </p>
        </div>
      )}
      {mobileText && (
        <div className="rounded-2xl border border-primary/30 bg-primary/5 px-5 py-3 text-left min-w-[140px]">
          <p className="text-[10px] uppercase tracking-wide text-primary/80">Mobile</p>
          <p className="text-2xl sm:text-3xl font-bold text-primary leading-tight">
            {mobileText}
            <span className="text-xs text-muted-foreground font-normal ml-1">{perMinLabel}</span>
          </p>
        </div>
      )}
    </div>
  );
}
